import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/core/auth/auth-store'
import { platformAdminRepository } from './platform-admin-repository'

export const isAdminKeys = {
  byUid: (uid: string) => ['platform-admin', uid] as const,
  byEmail: (email: string) => ['platform-admin', email] as const,
}

/**
 * Whether the current user is a platform admin.
 *
 * Mirrors the Firestore rule `isAdmin()` exactly: requires both membership
 * in `platform_config/admins.emails` AND a verified email. The rule rejects
 * unverified emails (any write returns insufficient-permissions), so if the
 * client doesn't match, an unverified admin would see the panel render then
 * hit silent rules failures on every mutation — confusing dead-end UX. The
 * caller can distinguish "not in list" from "in list but unverified" via
 * `isUnverifiedAdmin` to render a clearer remediation screen.
 */
export function useIsPlatformAdmin(): {
  isAdmin: boolean
  isUnverifiedAdmin: boolean
  isLoading: boolean
} {
  const user = useAuthStore((s) => s.user)
  const uid = user?.uid ?? null
  const email = user?.email ?? null
  const emailVerified = user?.emailVerified ?? false

  const query = useQuery({
    queryKey: uid ? isAdminKeys.byUid(uid) : ['platform-admin', 'none'],
    queryFn: () => platformAdminRepository.isAdmin(email),
    enabled: !!uid,
    staleTime: 5 * 60_000,
  })

  const inAdminList = query.data ?? false
  return {
    isAdmin: inAdminList && emailVerified,
    isUnverifiedAdmin: inAdminList && !emailVerified,
    isLoading: query.isLoading,
  }
}
